import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RotateCcw, Send, CheckCircle, X, Paperclip, UserCheck, AlertTriangle, Search, RefreshCw } from 'lucide-react';
import { apiCall } from './Api';

/**
 * WorkflowChatPreview - React chat widget with SSE (Server-Sent Events)
 *
 * PERFORMANCE REVISION:
 *  - Primary: SSE via EventSource — ONE persistent connection, server pushes
 *  - Fallback: 5s polling if SSE fails (e.g. browser/proxy doesn't support SSE)
 *  - No more 1000 req/sec from 1000 users — now 1000 persistent TCP connections
 *
 * Props:
 *  - workflowId: Workflow ID
 *  - apiEndpoint: Custom API base URL (only needed for external widget embedding)
 *  - apiKey: API key for public access (external widget)
 *  - authToken: JWT token for admin access
 *  - userId: Optional user ID (auto-generated if not provided)
 *  - onClose: Optional close handler
 */
const WorkflowChatPreview = ({
  workflowId,
  apiEndpoint,
  apiKey,
  authToken,
  onClose = null,
  userId: propUserId
}) => {
  // State
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [waitingForInput, setWaitingForInput] = useState(false);
  const [variables, setVariables] = useState({});
  const [userId, setUserId] = useState(propUserId || '');
  const [isInitialized, setIsInitialized] = useState(false);
  const [connectionMode, setConnectionMode] = useState('connecting'); // 'sse' | 'poll' | 'connecting'
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [userLanguage, setUserLanguage] = useState('en');
  const [botProcessing, setBotProcessing] = useState(false); // typing indicator

  // Refs
  const eventSourceRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isInitializingRef = useRef(false);

  // Whether we're running in external widget mode (custom endpoint + API key auth)
  const isExternalWidget = !!apiEndpoint;
  const baseUrl = apiEndpoint || '';

  // ==================== Unified API caller ====================
  // Internal (admin) mode: delegates to apiCall which handles base URL + JWT auth
  // External widget mode: uses raw fetch with apiEndpoint + API key header
  const callApi = useCallback((path, options = {}) => {
    if (isExternalWidget) {
      const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      } else if (apiKey && !apiKey.endsWith('...')) {
        headers['X-API-Key'] = apiKey;
      }
      return fetch(`${baseUrl}${path}`, { ...options, headers });
    }
    // Internal mode: apiCall handles base URL + auth automatically
    return apiCall(path, options);
  }, [isExternalWidget, baseUrl, authToken, apiKey]);

  // Build query string for auth (SSE can't send custom headers)
  const getAuthQueryParam = useCallback(() => {
    if (authToken) return `&token=${encodeURIComponent(authToken)}`;
    if (apiKey && !apiKey.endsWith('...')) return `&api_key=${encodeURIComponent(apiKey)}`;
    return '';
  }, [apiKey, authToken]);

  // ==================== User ID ====================
  useEffect(() => {
    if (!userId) {
      const stored = sessionStorage.getItem('workflow_user_id');
      if (stored) {
        setUserId(stored);
      } else {
        const newId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setUserId(newId);
        sessionStorage.setItem('workflow_user_id', newId);
      }
    }
  }, [userId]);

  // ==================== Init on mount ====================
  useEffect(() => {
    if (workflowId && userId && !isInitialized && !isInitializingRef.current) {
      initConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, userId, isInitialized]);

  // ==================== Cleanup on unmount ====================
  useEffect(() => {
    return () => {
      closeSSE();
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== Auto-scroll ====================
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages]);

  // ==================== SSE Connection ====================
  const connectSSE = useCallback(() => {
    closeSSE();
    stopPolling();

    const url = `${isExternalWidget ? baseUrl : window.location.origin}/api/chatbot/workflows/${workflowId}/web/stream?user_id=${encodeURIComponent(userId)}${getAuthQueryParam()}`;

    console.log('[Chat] Opening SSE connection...');
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.messages && data.messages.length > 0) {
          const newMsgs = data.messages.map(msg => ({
            type: 'bot',
            content: msg.content,
            message_type: msg.type,
            buttons: msg.buttons || [],
            options: msg.options || [],
            carousel_items: msg.carousel_items || [],
            media_url: msg.media_url,
            timestamp: new Date(msg.timestamp || Date.now())
          }));
          setMessages(prev => [...prev, ...newMsgs]);
          setBotProcessing(false);
        }

        if (data.session) {
          setWaitingForInput(data.session.waiting_for_input);
          setVariables(data.session.variables || {});
        }
      } catch (err) {
        console.error('[Chat] SSE parse error:', err);
      }
    });

    es.addEventListener('heartbeat', () => {
      // Keep-alive — nothing to do
    });

    es.onopen = () => {
      console.log('[Chat] SSE connected');
      setConnectionMode('sse');
    };

    es.onerror = (err) => {
      console.warn('[Chat] SSE error, falling back to polling:', err);
      closeSSE();
      startPolling();
      setConnectionMode('poll');
    };
  }, [baseUrl, workflowId, userId, getAuthQueryParam]);

  const closeSSE = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('[Chat] Closing SSE');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // ==================== Polling Fallback ====================
  const startPolling = useCallback(() => {
    stopPolling();
    pollingIntervalRef.current = setInterval(pollForMessages, 5000); // 5s instead of 1s
    console.log('[Chat] Polling started (5s fallback)');
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const pollForMessages = async () => {
    if (!workflowId || !userId) return;
    try {
      const response = await callApi(
        `/api/chatbot/workflows/${workflowId}/web/poll?user_id=${encodeURIComponent(userId)}`
      );
      if (!response.ok) return;

      const data = await response.json();
      if (data.messages && data.messages.length > 0) {
        const newMsgs = data.messages.map(msg => ({
          type: 'bot',
          content: msg.content,
          message_type: msg.type,
          buttons: msg.buttons || [],
          options: msg.options || [],
          carousel_items: msg.carousel_items || [],
          media_url: msg.media_url,
          timestamp: new Date(msg.timestamp || Date.now())
        }));
        setMessages(prev => [...prev, ...newMsgs]);
        setBotProcessing(false);
      }
      if (data.session) {
        setWaitingForInput(data.session.waiting_for_input);
        setVariables(data.session.variables || {});
      }
    } catch (error) {
      console.error('[Chat] Poll error:', error);
    }
  };

  // ==================== Initialization ====================
  const initConversation = async () => {
    if (isInitialized || isInitializingRef.current) return;
    isInitializingRef.current = true;

    try {
      const response = await callApi(
        `/api/chatbot/workflows/${workflowId}/web/init?user_id=${encodeURIComponent(userId)}`,
        { method: 'POST' }
      );
      console.log('[Chat] Init via', isExternalWidget ? 'external widget' : 'apiCall');

      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');
        console.error(`[Chat] Init failed: ${response.status} ${response.statusText}`, errorBody);
        throw new Error(`Init failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.messages && data.messages.length > 0) {
        const formatted = data.messages.map(msg => ({
          type: 'bot',
          content: msg.content,
          message_type: msg.type,
          buttons: msg.buttons || [],
          options: msg.options || [],
          carousel_items: msg.carousel_items || [],
          media_url: msg.media_url,
          timestamp: new Date(msg.timestamp || Date.now())
        }));
        setMessages(formatted);
      }

      if (data.session) {
        setWaitingForInput(data.session.waiting_for_input);
        setVariables(data.session.variables || {});
      }

      setIsInitialized(true);
      isInitializingRef.current = false;

      // Try SSE first, falls back to polling on error
      connectSSE();

    } catch (error) {
      console.error('[Chat] Init error:', error);
      isInitializingRef.current = false;
    }
  };

  // ==================== Send Message ====================
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !waitingForInput) return;

    const text = inputMessage.trim();
    setMessages(prev => [...prev, { type: 'user', content: text, timestamp: new Date() }]);
    setInputMessage('');
    setWaitingForInput(false);
    setBotProcessing(true);

    try {
      const response = await callApi(
        `/api/chatbot/workflows/${workflowId}/web/message?user_id=${encodeURIComponent(userId)}`,
        { method: 'POST', body: JSON.stringify({ text }) }
      );
      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();

      if (data.status === 'rejected') {
        console.warn('[Chat] Message rejected:', data.reason);
        setMessages(prev => prev.slice(0, -1));
        setBotProcessing(false);
        if (data.session) {
          setWaitingForInput(data.session.waiting_for_input);
          setVariables(data.session.variables || {});
        }
      }
      // botProcessing will be cleared when bot messages arrive via SSE/poll
    } catch (error) {
      console.error('[Chat] Send error:', error);
      setBotProcessing(false);
    }
  };

  // ==================== Button Click ====================
  // Track which message indices have already had a button/option clicked
  const clickedMessageIndices = useRef(new Set());

  const handleButtonClick = async (button, messageIndex) => {
    // Prevent double-click on same button group
    if (clickedMessageIndices.current.has(messageIndex)) {
      console.log('[Chat] Button group already clicked, ignoring');
      return;
    }

    // Mark this message's buttons as clicked (locally)
    clickedMessageIndices.current.add(messageIndex);

    // Show user message optimistically
    setMessages(prev => [...prev, { type: 'user', content: button.text, timestamp: new Date() }]);
    setWaitingForInput(false);
    setBotProcessing(true);

    try {
      const response = await callApi(
        `/api/chatbot/workflows/${workflowId}/web/button?user_id=${encodeURIComponent(userId)}`,
        {
          method: 'POST',
          body: JSON.stringify({
            text: button.text,
            value: button.value || button.text,
            next_step: button.next_step
          })
        }
      );
      if (!response.ok) throw new Error('Failed to send button click');

      const data = await response.json();

      if (data.status === 'rejected') {
        console.warn('[Chat] Button click rejected:', data.reason);
        setMessages(prev => prev.slice(0, -1));
        setBotProcessing(false);
        if (data.session) {
          setWaitingForInput(data.session.waiting_for_input);
          setVariables(data.session.variables || {});
        }
      }

    } catch (error) {
      console.error('[Chat] Button click error:', error);
      clickedMessageIndices.current.delete(messageIndex);
      setBotProcessing(false);
    }
  };

  const handleOptionClick = (option, messageIndex) => handleButtonClick(option, messageIndex);

  // ==================== File Upload ====================
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show optimistic user message
    setMessages(prev => [...prev, {
      type: 'user',
      content: `📎 ${file.name}`,
      message_type: 'file',
      file_name: file.name,
      file_size: file.size,
      timestamp: new Date()
    }]);
    setWaitingForInput(false);
    setBotProcessing(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // File upload needs FormData — callApi would add wrong Content-Type for external mode
      // For internal mode, apiCall also shouldn't set Content-Type (browser sets multipart boundary)
      const uploadHeaders = {};
      if (isExternalWidget) {
        if (authToken) {
          uploadHeaders['Authorization'] = `Bearer ${authToken}`;
        } else if (apiKey && !apiKey.endsWith('...')) {
          uploadHeaders['X-API-Key'] = apiKey;
        }
      }

      const uploadUrl = `/api/chatbot/workflows/${workflowId}/web/upload?user_id=${encodeURIComponent(userId)}`;
      const response = isExternalWidget
        ? await fetch(`${baseUrl}${uploadUrl}`, { method: 'POST', headers: uploadHeaders, body: formData })
        : await apiCall(uploadUrl, { method: 'POST', body: formData, headers: { 'Content-Type': undefined } });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      // Bot response will arrive via SSE/poll
    } catch (error) {
      console.error('[Chat] Upload error:', error);
      setMessages(prev => [...prev, {
        type: 'bot', content: `⚠️ Upload failed: ${error.message}`,
        message_type: 'text', timestamp: new Date()
      }]);
      setBotProcessing(false);
    }

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Helper to get auth token
  // ==================== Restart ====================
  const restartConversation = async () => {
    closeSSE();
    stopPolling();
    setMessages([]);
    setVariables({});
    setWaitingForInput(false);
    setBotProcessing(false);
    setIsInitialized(false);
    setConnectionMode('connecting');
    isInitializingRef.current = false;
    clickedMessageIndices.current = new Set();

    const newId = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('workflow_user_id', newId);
    setUserId(newId);

    setTimeout(() => initConversation(), 100);
  };

  const handleRestart = () => {
    sessionStorage.removeItem('chatbot_user_id');
    restartConversation();
  };

  const handleClose = () => {
    closeSSE();
    stopPolling();
    if (onClose) onClose();
  };

  // ==================== History ====================
  const fetchConversationHistory = async () => {
    if (!userId || !workflowId) return;
    try {
      setHistoryLoading(true);
      const response = await callApi(
        `/api/chatbot/workflows/${workflowId}/sessions/web/${userId}/history`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          const formatted = (data.history || []).map(msg => ({
            type: msg.type === 'user' ? 'user' : 'bot',
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            metadata: msg.metadata || {},
            buttons: msg.metadata?.buttons,
            options: msg.metadata?.options,
            message_type: msg.metadata?.message_type
          }));
          setConversationHistory(formatted);
        }
      }
    } catch (error) {
      console.error('[Chat] History fetch error:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ==================== History Modal (Enhanced Conversation Replay) ====================
  const [historySearch, setHistorySearch] = useState('');

  const filteredHistory = conversationHistory.filter(msg => {
    if (!historySearch) return true;
    return msg.content && msg.content.toLowerCase().includes(historySearch.toLowerCase());
  });

  const HistoryModal = () => {
    if (!showHistory) return null;
    return (
      <div className="absolute inset-0 bg-white z-10 flex flex-col">
        <div className="px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="font-semibold">Conversation Replay</h3>
          </div>
          <button onClick={() => { setShowHistory(false); setHistorySearch(''); }} className="text-white hover:text-purple-200 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search bar */}
        <div className="px-4 py-2 bg-white border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              placeholder="Search messages..."
              className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {historyLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="text-sm text-gray-600 mt-2">Loading history...</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{historySearch ? 'No messages match your search' : 'No conversation history available'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredHistory.map((message, index) => {
                const isUser = message.type === 'user';
                const isAgent = message.type === 'agent';
                const isFile = message.content && message.content.startsWith('[File:');
                const isValidationError = message.content && message.content.startsWith('⚠️') && !isUser;

                return (
                  <div key={index} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg p-3 shadow-sm ${
                      isUser
                        ? 'bg-blue-500 text-white'
                        : isAgent
                        ? 'bg-amber-50 text-gray-800 border border-amber-300'
                        : isValidationError
                        ? 'bg-red-50 text-gray-800 border border-red-200'
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}>
                      {/* Agent badge */}
                      {isAgent && (
                        <div className="flex items-center text-xs text-amber-600 mb-1 font-medium">
                          <UserCheck className="h-3 w-3 mr-1" />
                          Live Agent
                        </div>
                      )}

                      {/* Bot badge */}
                      {!isUser && !isAgent && (
                        <div className="text-xs text-gray-400 mb-1">🤖 Bot</div>
                      )}

                      {/* File message */}
                      {isFile ? (
                        <div className="flex items-center space-x-2">
                          <Paperclip className="h-4 w-4 flex-shrink-0" />
                          <span className="text-sm">{message.content}</span>
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      )}

                      {/* Buttons / options in history */}
                      {message.buttons && message.buttons.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {message.buttons.map((btn, idx) => (
                            <div key={idx} className="text-xs bg-gray-100 text-gray-700 rounded px-2 py-1">
                              {btn.text || btn}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Metadata */}
                      {message.metadata && message.metadata.file_info && (
                        <div className="mt-1 flex items-center text-xs opacity-70 space-x-1">
                          <Paperclip className="h-3 w-3" />
                          <span>{message.metadata.file_info.filename}</span>
                        </div>
                      )}

                      {/* Timestamp */}
                      <div className={`text-xs mt-1 ${isUser ? 'text-blue-200' : 'text-gray-400'}`}>
                        {message.timestamp instanceof Date
                          ? message.timestamp.toLocaleString()
                          : message.timestamp
                          ? new Date(message.timestamp).toLocaleString()
                          : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-3 bg-white border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {historySearch
                ? `${filteredHistory.length} of ${conversationHistory.length} messages`
                : `${conversationHistory.length} messages`
              }
            </span>
            <button onClick={fetchConversationHistory} className="text-purple-600 hover:text-purple-700 flex items-center space-x-1">
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== Render ====================
  return (
    <div className="flex flex-col h-full max-h-full bg-white relative">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h3 className="font-semibold">Live Preview (API)</h3>
            <p className="text-xs text-blue-100">
              Connected via {connectionMode === 'sse' ? '⚡ SSE (real-time)' : connectionMode === 'poll' ? '🔄 Polling (5s)' : '⏳ Connecting...'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* Language selector */}
            <select
              value={userLanguage}
              onChange={async (e) => {
                const lang = e.target.value;
                setUserLanguage(lang);
                // Persist language to backend
                try {
                  await callApi(
                    `/api/chatbot/workflows/${workflowId}/web/set-language?user_id=${encodeURIComponent(userId)}&language=${lang}`,
                    { method: 'POST' }
                  );
                } catch (err) { /* ignore */ }
              }}
              className="text-xs bg-blue-500 bg-opacity-30 text-white rounded px-1.5 py-1 border border-blue-400 border-opacity-40 cursor-pointer"
              title="Language"
            >
              <option value="en" className="text-gray-800">🌐 EN</option>
              <option value="es" className="text-gray-800">🌐 ES</option>
              <option value="fr" className="text-gray-800">🌐 FR</option>
              <option value="de" className="text-gray-800">🌐 DE</option>
              <option value="hi" className="text-gray-800">🌐 HI</option>
              <option value="ar" className="text-gray-800">🌐 AR</option>
              <option value="pt" className="text-gray-800">🌐 PT</option>
              <option value="zh" className="text-gray-800">🌐 ZH</option>
              <option value="ja" className="text-gray-800">🌐 JA</option>
            </select>
            <button
              onClick={() => { setShowHistory(true); fetchConversationHistory(); }}
              className="p-1.5 bg-blue-500 bg-opacity-30 rounded-md hover:bg-opacity-50 transition-colors"
              title="View History"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button onClick={handleRestart} className="p-1.5 bg-blue-500 bg-opacity-30 rounded-md hover:bg-opacity-50 transition-colors" title="Restart">
              <RotateCcw className="h-4 w-4" />
            </button>
            {onClose && (
              <button onClick={handleClose} className="p-1.5 bg-blue-500 bg-opacity-30 rounded-md hover:bg-opacity-50 transition-colors" title="Close">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-3">
        {messages.map((message, index) => (
          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${message.type === 'user' ? 'bg-blue-500 text-white rounded-tl-lg rounded-tr-lg rounded-bl-lg' : message.type === 'agent' ? 'bg-amber-50 text-gray-800 border border-amber-300 rounded-tl-lg rounded-tr-lg rounded-br-lg' : 'bg-white text-gray-800 border border-gray-200 rounded-tl-lg rounded-tr-lg rounded-br-lg'} p-3 shadow-sm`}>

              {/* Agent badge */}
              {message.type === 'agent' && (
                <div className="flex items-center text-xs text-amber-600 mb-1 font-medium">
                  <UserCheck className="h-3 w-3 mr-1" />
                  Live Agent
                </div>
              )}

              {/* File attachment */}
              {message.file_name && (
                <div className="flex items-center space-x-2 mb-1">
                  <Paperclip className="h-3 w-3 flex-shrink-0" />
                  <span className="text-sm truncate">{message.file_name}</span>
                  {message.file_size && (
                    <span className="text-xs opacity-70">({(message.file_size / 1024).toFixed(1)}KB)</span>
                  )}
                </div>
              )}

              {/* Validation error styling */}
              {message.content && message.content.startsWith('⚠️') && message.type === 'bot' && (
                <div className="flex items-start space-x-1 mb-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                </div>
              )}

              {/* Media */}
              {message.media_url && (
                <div className="mb-2">
                  {message.message_type === 'image' && <img src={message.media_url} alt="" className="rounded max-w-full" />}
                  {message.message_type === 'video' && <video src={message.media_url} controls className="rounded max-w-full" />}
                  {message.message_type === 'audio' && <audio src={message.media_url} controls className="w-full" />}
                </div>
              )}

              {/* Text */}
              {message.content && <p className="text-sm whitespace-pre-wrap">{message.content}</p>}

              {/* Buttons */}
              {message.buttons && message.buttons.length > 0 && (
                <div className="mt-2 space-y-1">
                  {message.buttons.map((btn, idx) => (
                    <button
                      key={idx}
                      onClick={() => waitingForInput && !clickedMessageIndices.current.has(index) && handleButtonClick(btn, index)}
                      disabled={!waitingForInput || clickedMessageIndices.current.has(index)}
                      className={`w-full text-left text-sm px-3 py-2 rounded border transition-colors ${
                        waitingForInput && !clickedMessageIndices.current.has(index)
                          ? 'border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 cursor-pointer'
                          : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {btn.text}
                    </button>
                  ))}
                </div>
              )}

              {/* Options */}
              {message.options && message.options.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {message.options.map((opt, idx) => (
                    <button
                      key={idx}
                      onClick={() => waitingForInput && !clickedMessageIndices.current.has(index) && handleOptionClick(opt, index)}
                      disabled={!waitingForInput || clickedMessageIndices.current.has(index)}
                      className={`text-xs px-3 py-1 rounded-full transition-colors ${
                        waitingForInput && !clickedMessageIndices.current.has(index)
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {opt.text}
                    </button>
                  ))}
                </div>
              )}

              {/* Carousel */}
              {message.carousel_items && message.carousel_items.length > 0 && (
                <div className="mt-2 space-y-2">
                  {message.carousel_items.map((item, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                      {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-24 object-cover" />}
                      <div className="p-2">
                        <p className="font-medium text-sm">{item.title}</p>
                        {item.subtitle && <p className="text-xs text-gray-500">{item.subtitle}</p>}
                        {item.buttons && item.buttons.map((btn, bIdx) => (
                          <button
                            key={bIdx}
                            onClick={() => waitingForInput && handleButtonClick(btn)}
                            disabled={!waitingForInput}
                            className="mt-1 w-full text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                          >
                            {btn.text}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className={`text-xs mt-1 ${message.type === 'user' ? 'text-blue-200' : 'text-gray-400'}`}>
                {message.timestamp instanceof Date ? message.timestamp.toLocaleTimeString() : ''}
              </div>
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {botProcessing && (
          <div className="flex justify-start mb-3">
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center space-x-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Handoff Banner */}
      {variables._handoff_active && (
        <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 flex items-center space-x-2 text-sm text-amber-700 flex-shrink-0">
          <UserCheck className="h-4 w-4" />
          <span>You're connected with a live agent</span>
        </div>
      )}

      {/* Input */}
      <div className="p-3 bg-white border-t border-gray-200 flex-shrink-0">
        <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
          />
          {/* File upload button */}
          <button
            type="button"
            onClick={() => waitingForInput && fileInputRef.current?.click()}
            disabled={!waitingForInput}
            className="p-2 text-gray-400 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
            title="Attach file"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder={waitingForInput ? 'Type your message...' : 'Waiting for bot...'}
            disabled={!waitingForInput}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
          />
          <button
            type="submit"
            disabled={!waitingForInput || !inputMessage.trim()}
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>

      {/* Variables debug (dev mode) */}
      {Object.keys(variables).length > 0 && (
        <div className="px-3 py-2 bg-gray-100 border-t border-gray-200 flex-shrink-0">
          <details>
            <summary className="text-xs text-gray-500 cursor-pointer">Variables ({Object.keys(variables).length})</summary>
            <div className="mt-1 text-xs font-mono text-gray-600">
              {Object.entries(variables).map(([k, v]) => (
                <div key={k}><span className="text-purple-600">{k}</span>: {JSON.stringify(v)}</div>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* History Modal Overlay */}
      <HistoryModal />
    </div>
  );
};

export default WorkflowChatPreview;
